import { WarningOctagon } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DeleteAccountDialog } from "./DeleteAccountDialog";
import { useDeleteAccount } from "../../lib/queries/user/useDeleteAccount";
import { LOGOUT, SETTINGS_SUBSCRIPTION } from "@/lib/links";
import { cn } from "@/lib/utils";

interface DeleteAccountSectionProps {
  className?: string;
  hasActiveSubscription?: boolean;
}

export function DeleteAccountSection({
  className,
  hasActiveSubscription = false,
}: DeleteAccountSectionProps) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { mutateAsync, isPending, isError, reset } = useDeleteAccount({
    onSuccess: () => {
      navigate(LOGOUT);
    },
  });

  const handleConfirm = async () => {
    await mutateAsync();
  };

  return (
    <section
      className={cn(
        "p-6 border border-destructive/40 rounded-lg bg-destructive/5",
        className
      )}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <WarningOctagon
            className="mt-1 text-destructive"
            size={28}
            weight="duotone"
          />
          <div>
            <h3 className="text-lg font-semibold text-destructive">
              {t("deleteAccount.title")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("deleteAccount.description")}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 md:items-end">
          <DeleteAccountDialog
            onConfirm={handleConfirm}
            isLoading={isPending}
            errorMessage={isError ? t("deleteAccount.inlineError") : undefined}
            disabled={hasActiveSubscription}
            onDialogOpenChange={(open: boolean) => {
              if (!open) {
                reset();
              }
            }}
          />
          {hasActiveSubscription && (
            <p className="text-sm text-muted-foreground md:text-right">
              {t("deleteAccount.requiresCancellation")}{" "}
              <Link
                to={SETTINGS_SUBSCRIPTION}
                className="font-semibold text-primary hover:underline"
              >
                {t("deleteAccount.requiresCancellationLink")}
              </Link>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
